import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

export default function APIAccess() {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState(null);
  const [usage, setUsage] = useState({ today: 0, month: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const plan = user?.plan_key;
  const hasAPI = plan === 'pack_one' || plan === 'pack_two' || plan === 'pack_advanced';
  const isFullAPI = plan === 'pack_advanced';
  const dailyLimit = plan === 'pack_one' ? 500 : plan === 'pack_two' ? 2000 : null;
  const monthlyLimit = plan === 'pack_one' ? 10000 : plan === 'pack_two' ? 50000 : null;
  const isFullAccess = plan === 'pack_advanced';

  useEffect(() => {
    if (!hasAPI) { setLoading(false); return; }
    axios.get('/api/business/api-usage').then(r => {
      if (r.data.data) setUsage(r.data.data);
    }).catch(() => {});
    axios.get('/api/business/api-key').then(r => {
      setApiKey(r.data.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const generateKey = async () => {
    setGenerating(true);
    try {
      const r = await axios.post('/api/business/api-key/generate');
      setApiKey(r.data.data);
      setShowKey(true);
      toast.success('API key generated!');
    } catch { toast.error('Failed'); }
    finally { setGenerating(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const endpoints = [
    { method: 'GET', path: '/api/v1/visitors', desc: 'Get all leads/visitors', access: 'both' },
    { method: 'GET', path: '/api/v1/chats', desc: 'Get all chats', access: 'both' },
    { method: 'GET', path: '/api/v1/analytics', desc: 'Get analytics data', access: 'both' },
    { method: 'GET', path: '/api/v1/agents', desc: 'Get agents status', access: 'both' },
    { method: 'POST', path: '/api/v1/message', desc: 'Send a message', access: 'full' },
    { method: 'POST', path: '/api/v1/visitor', desc: 'Create a lead', access: 'full' },
    { method: 'PUT', path: '/api/v1/chat/:id/close', desc: 'Close a chat', access: 'full' },
  ];

  if (!hasAPI) {
    return (
      <DashLayout navItems={nav} role="Business Admin">
        <div style={{ padding: 28, textAlign: 'center', paddingTop: 80 }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>🔑</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>API Access</h2>
          <p style={{ color: '#555', fontSize: 14, marginBottom: 8 }}>API Access is available in Pack Two (Read Only) and Pack Advanced (Full Access).</p>
          <a href="/dashboard/billing" style={{ display: 'inline-block', padding: '12px 28px', background: '#00d55b', color: '#000', borderRadius: 10, fontWeight: 700, textDecoration: 'none', marginTop: 16 }}>Upgrade Plan →</a>
        </div>
      </DashLayout>
    );
  }

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>🔑 API Access</h1>
        <p style={{ color: '#555', fontSize: 13, marginBottom: 28 }}>
          {isFullAccess ? 'Full Access — Read + Write API' : 'Read Only API — Upgrade to Pack Advanced for full access'}
        </p>

        {/* API Key Card */}
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Your API Key</h3>
          {loading ? (
            <div style={{ color: '#555' }}>Loading...</div>
          ) : apiKey ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>API Key</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input readOnly value={showKey ? apiKey.api_key : '•'.repeat(40)}
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, background: '#0d0d0d', border: '1px solid #1e1e1e', color: '#00d55b', borderRadius: 8, padding: '10px 14px' }} />
                  <button onClick={() => setShowKey(!showKey)}
                    style={{ padding: '0 14px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                    {showKey ? '🙈' : '👁'}
                  </button>
                  <button onClick={() => copy(apiKey.api_key)}
                    style={{ padding: '0 14px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#888', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                    Copy
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ fontSize: 12, color: '#555' }}>Created: {new Date(apiKey.created_at).toLocaleDateString('en-IN')}</div>
                <button onClick={generateKey} disabled={generating}
                  style={{ padding: '6px 14px', background: 'rgba(255,68,68,0.1)', border: '1px solid #ff4444', color: '#ff4444', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>
                  🔄 Regenerate Key
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <p style={{ color: '#555', fontSize: 13, marginBottom: 16 }}>No API key generated yet</p>
              <button onClick={generateKey} disabled={generating}
                style={{ padding: '11px 28px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>
                {generating ? 'Generating...' : '🔑 Generate API Key'}
              </button>
            </div>
          )}
        </div>

        {/* Usage example */}
        {apiKey && (
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Usage Example</h3>
            <pre style={{ background: '#0d0d0d', borderRadius: 10, padding: 16, fontSize: 12, color: '#00d55b', overflowX: 'auto', lineHeight: 1.7 }}>{`// Get all leads
fetch('https://api.mschat.msdiscoveries.com/api/v1/visitors', {
  headers: {
    'X-API-Key': '${showKey ? apiKey.api_key : 'your_api_key_here'}'
  }
})
.then(r => r.json())
.then(data => console.log(data.visitors));`}</pre>
          </div>
        )}

        {/* Endpoints */}
        <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 15, fontWeight: 600 }}>Available Endpoints</h3>
            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: isFullAccess ? 'rgba(0,213,91,0.1)' : 'rgba(59,130,246,0.1)', color: isFullAccess ? '#00d55b' : '#3b82f6', fontWeight: 600 }}>
              {isFullAccess ? 'Full Access' : 'Read Only'}
            </span>
          </div>
          {endpoints.map(ep => {
            const available = ep.access === 'both' || isFullAccess;
            return (
              <div key={ep.path} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', borderBottom: '1px solid #0d0d0d', opacity: available ? 1 : 0.4 }}>
                <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: 'monospace', background: ep.method === 'GET' ? 'rgba(0,213,91,0.1)' : 'rgba(59,130,246,0.1)', color: ep.method === 'GET' ? '#00d55b' : '#3b82f6', flexShrink: 0 }}>
                  {ep.method}
                </span>
                <code style={{ fontSize: 12, color: '#fff', flex: 1 }}>{ep.path}</code>
                <span style={{ fontSize: 12, color: '#555' }}>{ep.desc}</span>
                {!available && <span style={{ fontSize: 11, color: '#ff4444' }}>Pack Advanced</span>}
              </div>
            );
          })}
        </div>
      </div>
    </DashLayout>
  );
}